import type { Job } from '../types';

const steps = [
  { key: 'extracting', label: 'Распаковка', color: 'bg-slate-400' },
  { key: 'ocr', label: 'OCR', color: 'bg-indigo-500' },
  { key: 'translating', label: 'Перевод', color: 'bg-blue-500' },
  { key: 'exporting', label: 'Экспорт', color: 'bg-cyan-500' },
  { key: 'done', label: 'Готово', color: 'bg-emerald-500' },
];

const stepOrder: Record<string, number> = {};
steps.forEach((s, i) => { stepOrder[s.key] = i; });

function getStepIndex(job: Job): number {
  if (job.status === 'completed') return steps.length;
  if (job.status === 'failed') {
    const step = (job.progress?.step as string) || '';
    return stepOrder[step] ?? 0;
  }
  const step = (job.progress?.step as string) || '';
  return stepOrder[step] ?? 0;
}

interface Props {
  job: Job;
  compact?: boolean;
}

export default function JobProgressBar({ job, compact }: Props) {
  const currentIdx = getStepIndex(job);
  const isFailed = job.status === 'failed';
  const isCompleted = job.status === 'completed';
  const progress = typeof job.progress?.progress === 'number' ? job.progress.progress : 0;

  if (compact) {
    return (
      <div className="flex items-center gap-1.5">
        {steps.map((step, i) => {
          const isDone = i < currentIdx;
          const isActive = i === currentIdx && !isCompleted && !isFailed;
          const color = isFailed && i === currentIdx
            ? 'bg-rose-500'
            : isDone || isCompleted
              ? step.color
              : isActive
                ? step.color
                : 'bg-gray-200 dark:bg-gray-600';

          return (
            <div
              key={step.key}
              title={step.label}
              className={`h-1.5 flex-1 rounded-full transition-all ${color} ${
                isActive ? 'animate-pulse' : ''
              }`}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1">
        {steps.map((step, i) => {
          const isDone = i < currentIdx;
          const isActive = i === currentIdx && !isCompleted && !isFailed;
          const failedHere = isFailed && i === currentIdx;

          const bgColor = failedHere
            ? 'bg-rose-500'
            : isDone || isCompleted
              ? step.color
              : isActive
                ? step.color
                : 'bg-gray-200 dark:bg-gray-600';

          // For active step, show partial fill based on progress
          const width = isActive ? Math.max(10, progress) : (isDone || isCompleted ? 100 : 0);

          return (
            <div key={step.key} className="flex-1" title={step.label}>
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${bgColor} ${
                    isActive ? 'animate-pulse' : ''
                  }`}
                  style={{ width: `${width}%` }}
                />
              </div>
              <p className={`text-[10px] mt-0.5 text-center ${
                isDone || isCompleted || isActive || failedHere
                  ? 'text-gray-700 dark:text-gray-200 font-medium'
                  : 'text-gray-400 dark:text-gray-500'
              }`}>
                {step.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
